import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'edit-with-prompt' as const;

const sourceImageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('url'),
    url: z.string().describe('URL of the source image'),
  }),
  z.object({
    type: z.literal('base64'),
    base64: z.string().describe('Base64-encoded image data'),
    mimeType: z
      .string()
      .describe('MIME type of the image (e.g. image/png, image/jpeg)'),
  }),
]);

const argumentZod = z.object({
  sourceImage: sourceImageSchema.describe('The source image to edit'),
  prompt: z
    .string()
    .describe(
      "Natural language instruction describing the desired edit (e.g., 'make the sky purple', 'add a hat to the person', 'change the background to a beach')"
    ),
  imageConfig: z
    .object({
      aspectRatio: z
        .enum([
          '1:1',
          '2:3',
          '3:2',
          '3:4',
          '4:3',
          '4:5',
          '5:4',
          '9:16',
          '16:9',
          '21:9',
        ])
        .optional(),
      imageSize: z.enum(['1K', '2K', '4K']).optional(),
      personGeneration: z
        .enum(['ALLOW_ALL', 'ALLOW_ADULT', 'ALLOW_NONE'])
        .optional(),
    })
    .optional()
    .describe('Optional image generation configuration'),
});

export const editWithPromptManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Edit image',
  prettyStatus: {
    executed: 'Edited image',
    executing: 'Editing image',
    failed: 'Failed to edit image',
  },
  llmTool: {
    name: toolName,
    description:
      'Edits an existing image based on a natural language prompt. Applies the requested modifications while preserving the parts of the image that should remain unchanged.',
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [{resource: 'SiteFile', action: 'manage'}],
  confirmBeforeExecuting: false,
  timeout: 120_000,
});
